import { useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Notify } from "@/components/ui/notify";
import { useProducts } from "@/features/products/hooks";
import {
  LOSS_TYPE_LABELS,
  PRODUCTION_ROLE_HINTS,
  PRODUCTION_ROLE_LABELS,
  type LossTypeValue,
  type ProductionRole,
} from "@/types/parameters.types";
import type { ParameterDetail } from "@/features/line-editor/hooks";
import type { ParamForm } from "./types";

interface Props {
  form: ParamForm;
  setForm: (updater: (f: ParamForm) => ParamForm) => void;
  /** O sentido é imutável depois de criado (FR-009): na edição, vira leitura. */
  editing: boolean;
  /** Contadores JÁ existentes nesta máquina — o que FR-035 usa para não oferecer o impossível. */
  existingCounters: ParameterDetail[];
  /** Id do contador em edição, para que ele não se exclua da própria lista. */
  editingId: string | null;
}

const ROLES: ProductionRole[] = ["PRODUCES", "CONSUMES", "SCRAPS"];
const LOSS_TYPES: LossTypeValue[] = ["REJECT", "WASTE", "REWORK"];

/**
 * Spec 081 US1 — o bloco que faz o contador dizer O QUE ele mede: qual produto, em que sentido, de
 * que tipo é a perda quando refuga, e quanto vale uma unidade lida na unidade da linha.
 *
 * A equivalência aparece preenchida com a do PRODUTO e só fica editável quando a unidade lida
 * difere da unidade de contagem dele. Com a mesma unidade não há o que converter, e deixar o campo
 * aberto criaria duas respostas para a mesma pergunta.
 */
export function CounterFields({ form, setForm, editing, existingCounters, editingId }: Props) {
  const productsQuery = useProducts();
  const { data: products = [], isError, error, refetch, isSuccess } = productsQuery;
  const product = products.find((p) => p.id === form.productId);
  const role = form.productionRole === "" ? null : form.productionRole;

  // FR-035 — produto que já tem contador DESTE sentido nesta máquina sai da lista. A rota recusaria
  // com 409, e oferecer uma opção que se sabe recusada gasta a viagem do gestor para nada. O
  // contador em edição não se exclui: o produto que ele já usa continua sendo escolha válida dele.
  const takenProductIds = new Set(
    existingCounters
      .filter((c) => c.id !== editingId && c.productionRole === role && c.productId != null)
      .map((c) => c.productId as string)
  );
  const availableProducts = products.filter(
    (p) => !takenProductIds.has(p.id) || p.id === form.productId
  );
  const unitDiffers =
    product !== undefined && form.unit.trim() !== "" && form.unit.trim() !== product.countingUnit;

  // FR-021 — o campo desabilitado NÃO pode continuar enviando o que tem dentro. Trocar o produto
  // (ou a unidade lida) para uma combinação que dispensa sobreposição travava a equivalência com o
  // valor antigo ainda em estado: o envio levava esse valor, a rota recusava com 422
  // `PARAMETER_FACTOR_SAME_UNIT`, e o único campo capaz de resolver estava desabilitado — beco sem
  // saída a não ser cancelar a edição inteira. Zerar quando deixa de valer é a mesma regra vista do
  // outro lado.
  //
  // ⚠️ Só depois que a lista de produtos CHEGA. `unitDiffers` depende de encontrar o produto, e com
  // a lista ainda vazia ele é `false` por ignorância, não por regra: no primeiro render de uma
  // sessão o efeito apagava a equivalência própria de um contador que a tinha, e o próximo salvar
  // — mesmo que só trocasse o nome — mandava `unitFactor: null`. A conversão passava a usar a
  // equivalência do produto sem nada em tela acusar.
  useEffect(() => {
    if (!isSuccess) return;
    if (!unitDiffers && form.unitFactor !== "") {
      setForm((f) => ({ ...f, unitFactor: "" }));
    }
  }, [isSuccess, unitDiffers, form.unitFactor, setForm]);

  if (isError) {
    return <Notify.QueryError error={error} onRetry={() => void refetch()} variant="inline" />;
  }

  return (
    <div className="space-y-3 rounded-md border border-dashed p-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        O que este contador mede
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="p-role" className="text-xs">
          Sentido
        </Label>
        {editing ? (
          <p className="text-sm">
            {role ? PRODUCTION_ROLE_LABELS[role] : "Sem sentido de produção"}
            <span className="block text-[11px] text-muted-foreground">
              O sentido não pode ser alterado. Remova e crie outro para trocar.
            </span>
          </p>
        ) : (
          <>
            <Select
              id="p-role"
              value={form.productionRole}
              onChange={(e) => {
                // O valor é lido AQUI, não dentro do updater: o select é controlado, e um updater
                // que fecha sobre o evento lê `target.value` depois do re-render, quando ele já
                // voltou ao valor antigo.
                const next = e.target.value as ParamForm["productionRole"];
                setForm((f) => ({
                  ...f,
                  productionRole: next,
                  // Sair de Refugo apaga o tipo de perda: ele não vale nos outros sentidos.
                  lossType: next === "SCRAPS" ? f.lossType : "",
                }));
              }}
              className="h-9 text-sm"
            >
              <option value="">Nenhum — contador comum</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {PRODUCTION_ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
            <p className="text-[11px] text-muted-foreground">
              {role
                ? PRODUCTION_ROLE_HINTS[role]
                : "Sem sentido, o contador só registra valor e não entra em nenhum indicador."}
            </p>
          </>
        )}
      </div>

      {role !== null ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="p-product" className="text-xs">
              Produto
            </Label>
            <Select
              id="p-product"
              value={form.productId}
              onChange={(e) => setForm((f) => ({ ...f, productId: e.target.value }))}
              className="h-9 text-sm"
            >
              <option value="">Nenhum — não separa por produto</option>
              {availableProducts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · contado em {p.countingUnit}
                </option>
              ))}
            </Select>
            {takenProductIds.size > 0 ? (
              <p className="text-[11px] text-muted-foreground">
                {takenProductIds.size === 1
                  ? "1 produto não aparece: já tem contador deste sentido nesta máquina."
                  : `${takenProductIds.size} produtos não aparecem: já têm contador deste sentido nesta máquina.`}
              </p>
            ) : null}
          </div>

          {role === "SCRAPS" ? (
            <div className="space-y-1.5">
              <Label htmlFor="p-loss-type" className="text-xs">
                Tipo de perda
              </Label>
              <Select
                id="p-loss-type"
                value={form.lossType}
                onChange={(e) =>
                  setForm((f) => ({ ...f, lossType: e.target.value as ParamForm["lossType"] }))
                }
                className="h-9 text-sm"
              >
                <option value="">Selecione…</option>
                {LOSS_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {LOSS_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Um sensor não escolhe o tipo a cada leitura: ele é declarado aqui, de uma vez.
              </p>
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="p-unit-factor" className="text-xs">
              Equivalência
            </Label>
            <Input
              id="p-unit-factor"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              placeholder="0"
              className="h-9 text-sm"
              value={form.unitFactor}
              disabled={!unitDiffers}
              onChange={(e) => setForm((f) => ({ ...f, unitFactor: e.target.value }))}
            />
            <p className="text-[11px] text-muted-foreground">
              {product === undefined
                ? "Declare o produto para definir a equivalência."
                : unitDiffers
                  ? `Quanto vale 1 ${form.unit.trim()} lido na unidade da linha. O produto declara ${String(product.unitFactor)} por ${product.countingUnit}.`
                  : `Vem do produto: ${String(product.unitFactor)} por ${product.countingUnit}. Para mudar, altere o produto no cadastro.`}
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}
